import * as React from 'react'

import { F0Alert, F0Heading, F0Text } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import type { PayrollConceptSelectFieldFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { usePayrollConceptCompensationUnits } from 'modules/payroll_bundle/hooks/usePayrollConceptCompensationUnits'

import type { PendingPerWorkedDayDefinitionValues } from './lib/additionalCompensationWizard.shared'

type CustomNumberInputProps = {
  value: number | null | undefined
  onChange: (value: number | undefined) => void
  onClear?: () => void
  label: string
  placeholder?: string
  error?: string
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  selectedPayrollConceptId?: string
  required?: boolean
}

function CustomNumberInput({
  value,
  onChange,
  onClear,
  label,
  placeholder,
  error,
  payrollConcepts,
  selectedPayrollConceptId,
  required = false,
}: CustomNumberInputProps) {
  const selectedConcept = React.useMemo(
    () => payrollConcepts.find((payrollConcept) => payrollConcept.id === selectedPayrollConceptId),
    [payrollConcepts, selectedPayrollConceptId]
  )
  const unit = usePayrollConceptCompensationUnits(selectedConcept?.unitType ?? 'unit')

  return (
    <NumberInput
      value={value ?? null}
      onChange={(numberValue: number | null) => {
        if (numberValue === null) {
          if (onClear) {
            onClear()
            return
          }

          onChange(undefined)
          return
        }

        onChange(numberValue)
      }}
      label={label}
      locale={i18n.locale}
      placeholder={placeholder}
      units={unit}
      error={error}
      size="md"
      required={required}
    />
  )
}

type AmountInputWrapperProps = Pick<
  CustomNumberInputProps,
  | 'value'
  | 'onChange'
  | 'onClear'
  | 'error'
  | 'payrollConcepts'
  | 'selectedPayrollConceptId'
  | 'required'
>

function FixedAmountInputWrapper({
  value,
  onChange,
  onClear,
  error,
  payrollConcepts,
  selectedPayrollConceptId,
  required,
}: AmountInputWrapperProps) {
  return (
    <CustomNumberInput
      value={value}
      onChange={onChange}
      label={i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.amount_strategy_inputs_fields.fixed_amount_input.label'
      )}
      onClear={onClear}
      error={error}
      placeholder={i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.placeholder'
      )}
      selectedPayrollConceptId={selectedPayrollConceptId}
      payrollConcepts={payrollConcepts}
      required={required}
    />
  )
}

function MaximumAmountInputWrapper({
  value,
  onChange,
  onClear,
  error,
  payrollConcepts,
  selectedPayrollConceptId,
}: AmountInputWrapperProps) {
  return (
    <CustomNumberInput
      value={value}
      onChange={onChange}
      label={i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.maximum_amount_per_cycle.label'
      )}
      error={error}
      placeholder={i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.variable_amount.placeholder'
      )}
      onClear={onClear}
      selectedPayrollConceptId={selectedPayrollConceptId}
      payrollConcepts={payrollConcepts}
    />
  )
}

function PerWorkedDayAmountInputWrapper({
  value,
  onChange,
  error,
  payrollConcepts,
  selectedPayrollConceptId,
  required,
}: AmountInputWrapperProps) {
  return (
    <CustomNumberInput
      value={value}
      onChange={onChange}
      label={i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day_amount.label'
      )}
      error={error}
      placeholder={i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.variable_amount.placeholder'
      )}
      selectedPayrollConceptId={selectedPayrollConceptId}
      payrollConcepts={payrollConcepts}
      required={required}
    />
  )
}

function PendingPerWorkedDayDefinition({
  payrollConcepts,
  selectedPayrollConceptId,
  values,
  onChange,
  error,
}: {
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  selectedPayrollConceptId?: string
  values: PendingPerWorkedDayDefinitionValues
  onChange: (values: Partial<PendingPerWorkedDayDefinitionValues>) => void
  error?: string
}) {
  return (
    <>
      <PerWorkedDayAmountInputWrapper
        value={values.amountPerDay}
        onChange={(amountPerDay) => onChange({ amountPerDay })}
        error={error}
        payrollConcepts={payrollConcepts}
        selectedPayrollConceptId={selectedPayrollConceptId}
        required
      />
    </>
  )
}

function PaymentFrequencyHeader() {
  return (
    <>
      <F0Heading
        variant="heading"
        content={i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.sections.payment_frequency.title'
        )}
      />

      <F0Text
        color="secondary"
        content={i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.sections.payment_frequency.description'
        )}
        variant="description"
      />
    </>
  )
}

function FirstPaymentInfoCallout() {
  return (
    <F0Alert
      variant="info"
      title={i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.first_payment_on.first_payment_same_month_as_effective_title'
      )}
      description={i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.first_payment_on.first_payment_same_month_as_effective_description'
      )}
    />
  )
}

export {
  FirstPaymentInfoCallout,
  FixedAmountInputWrapper,
  MaximumAmountInputWrapper,
  PaymentFrequencyHeader,
  PendingPerWorkedDayDefinition,
}
